// TaskProgressFloat.tsx — Floating progress widget for background Celery tasks.
// Renders a persistent bottom-right panel showing active import/export jobs
// with live polling. Minimisable, auto-dismisses after completion.
import { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Icon } from './icons';
import type { IconName } from './icons';

// ─── Task Tracker Context ─────────────────────────────────────────────────

export type TaskKind = 'import' | 'export';

export interface TrackedTask {
  id: string;
  kind: TaskKind;
  label: string;
  icon: IconName;
  startedAt: number;
}

interface TaskTrackerValue {
  tasks: TrackedTask[];
  addTask: (task: TrackedTask) => void;
  removeTask: (id: string) => void;
}

const TaskTrackerCtx = createContext<TaskTrackerValue | null>(null);

export function TaskTrackerProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<TrackedTask[]>([]);

  const addTask = useCallback((task: TrackedTask) => {
    setTasks((prev) => {
      if (prev.some((t) => t.id === task.id)) return prev;
      return [...prev, task];
    });
  }, []);

  const removeTask = useCallback((id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => ({ tasks, addTask, removeTask }), [tasks, addTask, removeTask]);

  return <TaskTrackerCtx.Provider value={value}>{children}</TaskTrackerCtx.Provider>;
}

export function useTaskTracker(): TaskTrackerValue {
  const ctx = useContext(TaskTrackerCtx);
  if (!ctx) throw new Error('useTaskTracker must be used within <TaskTrackerProvider>');
  return ctx;
}

// ─── Individual Task Row (with polling) ───────────────────────────────────

interface TaskProgress {
  pct: number;
  message: string;
  status?: string;
  download_url?: string;
}

function TaskRow({ task, onDone }: { task: TrackedTask; onDone: () => void }) {
  const [done, setDone] = useState(false);
  const endpoint =
    task.kind === 'import'
      ? `/api/master-data/bulk-jobs/${task.id}/progress/`
      : `/api/master-data/bulk-exports/${task.id}/progress/`;

  const { data } = useQuery<TaskProgress>({
    queryKey: ['task-float', task.kind, task.id],
    queryFn: () => apiFetch<TaskProgress>(endpoint),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return 2000;
      if (d.pct >= 100 || d.status === 'completed' || d.status === 'failed') return false;
      return 2000;
    },
  });

  const pct = data?.pct ?? 0;
  const message = data?.message ?? 'Starting…';

  // Auto-dismiss 5s after completion
  useEffect(() => {
    if (pct >= 100 && !done) {
      setDone(true);
      const t = setTimeout(onDone, 5000);
      return () => clearTimeout(t);
    }
  }, [pct, done, onDone]);

  const isError =
    message.toLowerCase().includes('failed') || message.toLowerCase().includes('error');
  const barColor = isError
    ? 'var(--error-main)'
    : pct >= 100
      ? 'var(--success-main)'
      : 'var(--primary-main)';

  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--divider)' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 6 }}
      >
        <Icon name={task.icon} size={15} color="var(--primary-main)" />
        <span
          style={{
            flex: 1,
            fontSize: 'var(--fs-body-sm)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {task.label}
        </span>
        <span
          style={{
            fontSize: 'var(--fs-caption)',
            fontWeight: 700,
            color: pct >= 100 ? 'var(--success-main)' : 'var(--text-secondary)',
          }}
        >
          {pct >= 100 ? '✓' : `${Math.round(pct)}%`}
        </span>
      </div>
      <div
        style={{
          height: 5,
          borderRadius: 'var(--radius-pill)',
          background: 'var(--grey-200)',
          overflow: 'hidden',
          marginBottom: 'var(--space-1)',
        }}
      >
        <div
          style={{
            width: `${Math.min(100, pct)}%`,
            height: '100%',
            borderRadius: 'var(--radius-pill)',
            background: barColor,
            transition:
              'width var(--duration-base) var(--ease), background var(--duration-base) var(--ease)',
          }}
        />
      </div>
      <div
        style={{
          fontSize: 'var(--fs-caption)',
          color: isError ? 'var(--error-dark)' : 'var(--text-secondary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {message}
      </div>
      {data?.download_url && pct >= 100 && (
        <a
          href={data.download_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            marginTop: 6,
            fontSize: 'var(--fs-caption)',
            fontWeight: 700,
            color: 'var(--primary-main)',
            textDecoration: 'none',
          }}
        >
          <Icon name="download" size={12} /> Download
        </a>
      )}
    </div>
  );
}

// ─── Floating Panel ───────────────────────────────────────────────────────

export function TaskProgressFloat() {
  const { tasks, removeTask } = useTaskTracker();
  const [minimised, setMinimised] = useState(false);

  if (tasks.length === 0) return null;

  const activeCount = tasks.length;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 92, // sits above the global quick-actions "+" FAB (bottom: 24)
        right: 24,
        width: minimised ? 220 : 320,
        background: 'var(--bg-card)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-z8)',
        zIndex: 10000,
        overflow: 'hidden',
        transition: 'width var(--duration-base) var(--ease)',
        border: '1px solid var(--divider)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: '10px 14px',
          background: 'var(--primary-main)',
          color: 'var(--primary-contrast)',
          cursor: 'pointer',
        }}
        onClick={() => setMinimised((v) => !v)}
      >
        <Icon name="layers" size={16} />
        <span style={{ flex: 1, fontSize: 'var(--fs-body-sm)', fontWeight: 700 }}>
          {activeCount} active task{activeCount !== 1 ? 's' : ''}
        </span>
        <span
          style={{
            display: 'inline-flex',
            transition: 'transform var(--duration-base) var(--ease)',
            transform: minimised ? 'rotate(180deg)' : 'none',
          }}
        >
          <Icon name="chevdown" size={14} />
        </span>
      </div>

      {/* Task list */}
      {!minimised && (
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} onDone={() => removeTask(task.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
